/**
 * Query options this screen owns, plus the shapes the shared factory gets
 * wrong.
 *
 * `src/api/queries.ts` is shared infrastructure owned by an earlier step and
 * is not edited here. Two of its payroll entries are typed against the wrong
 * DTO — verified against the running API:
 *
 *   • `qk.payroll.progress(id)` is declared `JobTicketDto`, but
 *     `GET /payroll/runs/{id}/progress` (P15) returns `RunProgressDto`
 *     (`{runId, ticketId, status, phase, processedWorkers, totalWorkers, …}`).
 *   • `qk.payroll.exports(p)` is declared `ExportBatchDto[]`, but
 *     `GET /payroll/runs/{id}/exports` (X2) returns `Paginated<ExportBatchDto>`
 *     — it is a paginated list like every other (§1.5, "without exception").
 *
 * Both are re-declared here with the real shape and a DISTINCT key segment, so
 * one cache entry is never read back under two different types.
 */
import { queryOptions } from '@tanstack/react-query';
import type {
  ExportBatchDto,
  JobTicketDto,
  Paginated,
  PayrollLineListItemDto,
  PayrollLineSourceDto,
  PayrollRegisterDto,
  PayrollRemarkDto,
  PayrollRunDto,
  PayslipDto,
  ReportGroupBy,
  RunProgressDto,
} from '@cropmate/shared';
import { api, type QueryParams, type WithETag } from '@/api/client';

/**
 * P13 — the run, WITH its `ETag`.
 *
 * §12.7: *"returns an `ETag` — send it back as `If-Match` on lock"*. The
 * shared `qk.payroll.run()` uses `api.get`, which drops response headers, so
 * the lock could only ever be sent without `If-Match` and would lose the
 * optimistic-concurrency check (`409 E_STALE_VERSION`). This screen uses
 * `getWithETag` instead and keeps the tag alongside the data.
 */
export function runQuery(runId: string) {
  return queryOptions({
    queryKey: ['payroll', 'run-with-etag', runId] as const,
    queryFn: () => api.getWithETag<PayrollRunDto>(`/payroll/runs/${runId}`),
    staleTime: 30_000,
  });
}

export type RunWithETag = WithETag<PayrollRunDto>;

/**
 * P20 — the worker list behind the breakdown.
 *
 * The shared `qk.payroll.lines({ runId, ...filters })` spreads its WHOLE
 * argument into the query string, so `runId` — a PATH parameter — is also sent
 * as `?runId=…`. The API validates query DTOs with `forbidNonWhitelisted`, so
 * that request is rejected outright:
 *
 *   GET /payroll/runs/{id}/lines?runId={id}&page=1
 *   → 400 E_VALIDATION  "property runId should not exist"
 *
 * Verified against the running API; the same request without `runId` returns
 * 200. The path parameter is therefore kept out of the params bag here.
 */
export function runLinesQuery(
  runId: string,
  filters: { page: number; pageSize: number; q?: string; teamId?: string; teamIds?: readonly string[] },
) {
  return queryOptions({
    queryKey: ['payroll', 'run-lines', runId, filters] as const,
    queryFn: () =>
      api.get<Paginated<PayrollLineListItemDto>>(`/payroll/runs/${runId}/lines`, {
        page: filters.page,
        pageSize: filters.pageSize,
        ...(filters.q ? { q: filters.q } : {}),
        ...(filters.teamId ? { teamId: filters.teamId } : {}),
        /* One comma-separated value — see `runPreviewRegisterQuery`. */
        ...(filters.teamIds && filters.teamIds.length > 0 ? { teamIds: filters.teamIds.join(',') } : {}),
      }),
    staleTime: 30_000,
  });
}

/**
 * P25a — the month-to-date PREVIEW list of a run that has not been computed. The server
 * recomputes the whole period on every call, so the 30 s `staleTime` is doing real work;
 * it is also about how long a preview stays honest while clerks are still typing. Under
 * `['payroll', …]` deliberately: an execute invalidates it with everything else.
 */
export function runPreviewLinesQuery(
  runId: string,
  filters: { page: number; pageSize: number; q?: string; teamId?: string; teamIds?: readonly string[] },
) {
  return queryOptions({
    queryKey: ['payroll', 'run-preview-lines', runId, filters] as const,
    queryFn: () =>
      api.get<Paginated<PayrollLineListItemDto>>(`/payroll/runs/${runId}/preview/lines`, {
        page: filters.page,
        pageSize: filters.pageSize,
        ...(filters.q ? { q: filters.q } : {}),
        ...(filters.teamId ? { teamId: filters.teamId } : {}),
        /* One comma-separated value — see `runPreviewRegisterQuery`. */
        ...(filters.teamIds && filters.teamIds.length > 0 ? { teamIds: filters.teamIds.join(',') } : {}),
      }),
    staleTime: 30_000,
  });
}

/** P25b — the month-to-date register from the same dry run, in T8's shape. */
export function runPreviewRegisterQuery(
  runId: string,
  filters: { page: number; pageSize: number; groupBy: ReportGroupBy; teamIds?: readonly string[] },
) {
  return queryOptions({
    queryKey: ['payroll', 'run-preview-register', runId, filters] as const,
    queryFn: () =>
      api.get<PayrollRegisterDto>(`/payroll/runs/${runId}/preview/register`, {
        page: filters.page,
        pageSize: filters.pageSize,
        groupBy: filters.groupBy,
        /* ONE comma-separated value, never a repeated key — qs turns a 21st repeat into an
         * object (the job reports' `teamIds`, `common/dto/team-ids.decorator.ts`). */
        ...(filters.teamIds && filters.teamIds.length > 0 ? { teamIds: filters.teamIds.join(',') } : {}),
      }),
    staleTime: 30_000,
  });
}

/** P25 — one worker's month-to-date payslip from the same dry run. */
export function runPreviewPayslipQuery(runId: string, workerId: string) {
  return queryOptions({
    queryKey: ['payroll', 'run-preview-payslip', runId, workerId] as const,
    queryFn: () => api.get<PayslipDto>(`/payroll/runs/${runId}/preview`, { workerId }),
    staleTime: 30_000,
  });
}

/**
 * P22 — G5, every wage traceable to the day's job paper.
 *
 * Same defect as above in `qk.payroll.sources`, which sends BOTH `runId` and
 * `workerId` as query params and earns a 400 with two problems.
 */
export function lineSourcesQuery(runId: string, workerId: string, pageSize = 200) {
  return queryOptions({
    queryKey: ['payroll', 'line-sources', runId, workerId, pageSize] as const,
    queryFn: () =>
      api.get<Paginated<PayrollLineSourceDto>>(
        `/payroll/runs/${runId}/lines/${workerId}/sources`,
        { pageSize },
      ),
    staleTime: 60_000,
  });
}

/** P15 — polled every 2 s while the engine is running (§12.7, NFR-2). */
export function runProgressQuery(runId: string) {
  return queryOptions({
    queryKey: ['payroll', 'run-progress', runId] as const,
    queryFn: () => api.get<RunProgressDto>(`/payroll/runs/${runId}/progress`),
    staleTime: 0,
  });
}

/** X2 — the export batches already generated for this run. */
export function runExportsQuery(runId: string, params: QueryParams = {}) {
  return queryOptions({
    queryKey: ['payroll', 'export-batches', runId, params] as const,
    queryFn: () => api.get<Paginated<ExportBatchDto>>(`/payroll/runs/${runId}/exports`, params),
    staleTime: 30_000,
  });
}

/** Z1 — every 202 is polled the same way. */
export function jobTicketQuery(ticketId: string) {
  return queryOptions({
    queryKey: ['jobs', 'ticket', ticketId] as const,
    queryFn: () => api.get<JobTicketDto>(`/jobs/${ticketId}`),
    staleTime: 0,
  });
}

/**
 * The rows `422 E_RUN_UNCONFIRMED_CONFIG` carries in `meta.unconfirmed[]`.
 *
 * Read off the running API — the error envelope's `meta` is
 * `Record<string, unknown>` in the contract, so the payload shape is declared
 * at the point of use rather than guessed at globally.
 */
export interface UnconfirmedRuleRow {
  table: string;
  id: string;
  scheme?: string;
  code?: string;
  label: string;
}

/** A run whose period is not yet closed reports its blockers up front. */
export const LOCK_BLOCKER_HELP: Readonly<Record<string, string>> = {
  E_SAME_RUNNER_LOCKER:
    'Separation of duty is switched on: the person who ran a payroll may not lock it. Ask another holder of Approve & lock to review and lock this run, or have an Admin switch the rule off under Admin → Configuration (payroll.require_separate_locker).',
  E_RUN_UNCONFIRMED_CONFIG:
    'The statutory rates are unconfirmed. The run computed on assumed rates so reconciliation can proceed, but it cannot be locked until the client confirms them.',
  E_RUN_STATE: 'This run is not in a state that can be locked. Compute it first.',
  E_NEGATIVE_NET:
    'A negative net pay cannot be disbursed, so the run cannot be locked with one on it. Open Adjustments, reduce or defer that worker’s deductions, run the payroll again, then lock.',
};

/**
 * P31 — one worker's remark for the run's PERIOD (migration 0058). Keyed by run here because the
 * screen is a run; every run of the period answers the same row. Not under `['payroll', …]`, so
 * an execute's broad invalidation does not refetch it — a re-run never changes a remark.
 */
export function runRemarkQuery(runId: string, workerId: string) {
  return queryOptions({
    queryKey: ['payroll-remark', runId, workerId] as const,
    queryFn: () => api.get<PayrollRemarkDto>(`/payroll/runs/${runId}/remarks/${workerId}`),
    staleTime: 60_000,
  });
}
